import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Quote } from '@/types/quote';
import { useToast } from '@/hooks/use-toast';

export const useQuotes = () => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('quotes')
        .select(`
          *,
          organization:organizations(id, name),
          contact:contacts(id, first_name, last_name, email),
          project:projects(id, name, project_number),
          charging_location:charging_locations(id, name),
          charging_opportunity:charging_opportunities(id, name)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuotes((data as unknown as Quote[]) || []);
    } catch (error: any) {
      console.error('Error fetching quotes:', error);
      toast({
        title: 'Fout bij laden',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createQuote = async (quoteData: {
    title: string;
    organization_id: string;
    contact_id?: string;
    project_id?: string;
    template_id?: string;
    body?: string;
    terms?: string;
    quote_number?: string;
  }) => {
    try {
      let quoteNumber = quoteData.quote_number;
      
      if (!quoteNumber) {
        const { data: numberData, error: numberError } = await supabase.rpc('generate_quote_number');
        if (numberError) throw numberError;
        quoteNumber = numberData;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Niet ingelogd');

      const { data, error } = await supabase
        .from('quotes')
        .insert({
          title: quoteData.title,
          organization_id: quoteData.organization_id,
          contact_id: quoteData.contact_id,
          project_id: quoteData.project_id,
          template_id: quoteData.template_id,
          body: quoteData.body,
          terms: quoteData.terms,
          quote_number: quoteNumber,
          owner_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Offerte aangemaakt',
        description: `Offerte ${quoteNumber} is aangemaakt`,
      });

      await fetchQuotes();
      return data;
    } catch (error: any) {
      console.error('Error creating quote:', error);
      toast({
        title: 'Fout bij aanmaken',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteQuote = async (quoteId: string) => {
    try {
      const { error } = await supabase
        .from('quotes')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', quoteId);

      if (error) throw error;

      toast({
        title: 'Offerte verwijderd',
      });

      await fetchQuotes();
    } catch (error: any) {
      console.error('Error deleting quote:', error);
      toast({
        title: 'Fout bij verwijderen',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  return {
    quotes,
    loading,
    refetch: fetchQuotes,
    createQuote,
    deleteQuote,
  };
};
